import React, { useState, useEffect } from 'react';
import { 
  Mail, Phone, MessageSquare, Bot, AlertTriangle, Filter,
  Clock, ChevronRight, Briefcase, User
} from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel icons mapping
const channelIcons = {
  email: Mail,
  call: Phone,
  client_portal: MessageSquare,
  assistant_life: Bot,
  emergency: AlertTriangle
};

// Actor labels as per spec
const actorLabels = {
  client: 'Client',
  assistant: 'Assistant',
  system: 'System',
  user: 'User'
};

// Mock data for demonstration
const mockThreads = [
  {
    id: '1',
    subject: 'Bathroom renovation query',
    channel: 'email',
    client_name: 'Sarah Johnson',
    client_id: 'c1',
    job_id: 'j1',
    job_title: 'Bathroom Renovation',
    status: 'normal',
    is_emergency: false,
    is_unread: false,
    message_count: 4,
    last_message_at: new Date().toISOString(),
    messages: [
      {
        id: 'm1',
        actor_type: 'client',
        content: 'Hi, I would like to get a quote for bathroom renovation including new tiles and fixtures.',
        sender_name: 'Sarah Johnson',
        created_at: new Date(Date.now() - 86400000).toISOString()
      },
      {
        id: 'm2',
        actor_type: 'user',
        content: 'Thank you for your enquiry. We would be happy to arrange a site visit to discuss your requirements.',
        sender_name: 'SKB Interiors',
        created_at: new Date(Date.now() - 43200000).toISOString()
      }
    ]
  },
  {
    id: '2',
    subject: 'BURST PIPE - Kitchen flooding',
    channel: 'emergency',
    client_name: 'Mike Thompson',
    client_id: 'c2',
    job_id: null,
    status: 'escalating',
    is_emergency: true,
    is_unread: true,
    message_count: 6,
    last_message_at: new Date(Date.now() - 3600000).toISOString(),
    messages: [
      {
        id: 'm3',
        actor_type: 'client',
        content: 'URGENT - Water pipe has burst in kitchen. Water everywhere. Need help immediately!',
        sender_name: 'Mike Thompson',
        created_at: new Date(Date.now() - 7200000).toISOString()
      },
      {
        id: 'm4',
        actor_type: 'system',
        content: 'Emergency received and logged',
        created_at: new Date(Date.now() - 7190000).toISOString(),
        is_system_event: true
      },
      {
        id: 'm5',
        actor_type: 'system',
        content: 'Owner notified via push notification',
        created_at: new Date(Date.now() - 7180000).toISOString(),
        is_system_event: true
      },
      {
        id: 'm6',
        actor_type: 'system',
        content: 'On-call trade notified via SMS',
        created_at: new Date(Date.now() - 7170000).toISOString(),
        is_system_event: true
      }
    ]
  },
  {
    id: '3',
    subject: 'Invoice query - INV-1045',
    channel: 'client_portal',
    client_name: 'David Williams',
    client_id: 'c3',
    job_id: 'j2',
    job_title: 'Kitchen Refit',
    status: 'normal',
    is_emergency: false,
    is_unread: true,
    message_count: 2,
    last_message_at: new Date(Date.now() - 7200000).toISOString(),
    messages: [
      {
        id: 'm7',
        actor_type: 'client',
        content: 'Can you explain the additional charges on invoice INV-1045?',
        sender_name: 'David Williams',
        created_at: new Date(Date.now() - 7200000).toISOString()
      }
    ]
  }
];

const mockStats = {
  total_messages: 156,
  unread: 12,
  emergencies: 1,
  threads: 34
};

export function Communications() {
  const { user } = useAuth();
  const [threads, setThreads] = useState(mockThreads);
  const [stats, setStats] = useState(mockStats);
  const [selectedThread, setSelectedThread] = useState(null);
  const [filter, setFilter] = useState({
    channel: null,
    job_id: null,
    client_id: null,
    status: null
  });
  const [replyContent, setReplyContent] = useState('');

  const formatTimestamp = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-GB');
  };

  // Filter and sort threads - Emergency threads ALWAYS pinned to top (cannot be reordered)
  const filteredThreads = threads.filter(thread => {
    if (filter.channel && thread.channel !== filter.channel) return false;
    if (filter.job_id && thread.job_id !== filter.job_id) return false;
    if (filter.client_id && thread.client_id !== filter.client_id) return false;
    if (filter.status && thread.status !== filter.status) return false;
    return true;
  }).sort((a, b) => {
    // Emergency threads ALWAYS first - cannot be dismissed or reordered manually
    if (a.is_emergency && !b.is_emergency) return -1;
    if (!a.is_emergency && b.is_emergency) return 1;
    return new Date(b.last_message_at) - new Date(a.last_message_at);
  });

  const handleSendReply = () => {
    if (!replyContent.trim() || !selectedThread) return;
    
    const newMessage = {
      id: `m${Date.now()}`,
      actor_type: 'user',
      content: replyContent,
      sender_name: user?.name || 'User',
      created_at: new Date().toISOString()
    };
    
    setThreads(prev => prev.map(t => {
      if (t.id === selectedThread.id) {
        return {
          ...t,
          messages: [...t.messages, newMessage],
          message_count: t.message_count + 1,
          last_message_at: new Date().toISOString(),
          is_unread: false
        };
      }
      return t;
    }));
    
    setSelectedThread(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage],
      is_unread: false
    }));
    
    setReplyContent('');
  };

  const handleSelectThread = (thread) => {
    setSelectedThread(thread);
    // Mark as read
    setThreads(prev => prev.map(t => 
      t.id === thread.id ? { ...t, is_unread: false } : t
    ));
  };

  return (
    <div className="page" data-testid="communications-hub">
      <div className="page-header">
        <div className="page-header-content">
          <h1 data-testid="communications-title">Communications</h1>
          <p>Single, auditable view of all communications across jobs, clients, and channels</p>
        </div>
      </div>

      {/* Read-Only Filters */}
      <div className="glass-panel" style={{ marginBottom: 'var(--space-lg)', padding: 'var(--space-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
          <Filter size={16} style={{ color: 'var(--text-muted)' }} />
          <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Filter:</span>
          
          {/* Channel Filter */}
          <select 
            className="form-input" 
            style={{ width: 'auto', padding: '6px 12px' }}
            value={filter.channel || ''}
            onChange={(e) => setFilter(prev => ({ ...prev, channel: e.target.value || null }))}
            data-testid="filter-channel"
          >
            <option value="">All Channels</option>
            <option value="email">Email</option>
            <option value="call">Call</option>
            <option value="client_portal">Client Portal</option>
            <option value="assistant_life">Assistant</option>
            <option value="emergency">Emergency</option>
          </select>
          
          {/* Status Filter */}
          <select 
            className="form-input" 
            style={{ width: 'auto', padding: '6px 12px' }}
            value={filter.status || ''}
            onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value || null }))}
            data-testid="filter-status"
          >
            <option value="">All Status</option>
            <option value="normal">Normal</option>
            <option value="escalating">Emergency - Escalating</option>
            <option value="awaiting_acceptance">Emergency - Awaiting Acceptance</option>
          </select>
          
          {filter.channel || filter.status ? (
            <button 
              className="btn btn-ghost"
              onClick={() => setFilter({ channel: null, job_id: null, client_id: null, status: null })}
              style={{ fontSize: '0.75rem' }}
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {/* Main Layout: Left (List) | Centre (Thread) | Right (Context) */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: selectedThread ? '320px 1fr 280px' : '1fr', 
        gap: 'var(--space-lg)',
        minHeight: '500px'
      }}>
        
        {/* LEFT: Conversation List (Unified Inbox) */}
        <div className="glass-panel" style={{ maxHeight: '600px', overflow: 'auto', padding: 0 }} data-testid="conversation-list">
          <div style={{ padding: 'var(--space-md)', borderBottom: '1px solid var(--glass-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                Conversations
              </span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {filteredThreads.length} threads
              </span>
            </div>
          </div>
          
          {filteredThreads.length > 0 ? (
            <div>
              {filteredThreads.map(thread => {
                const ChannelIcon = channelIcons[thread.channel] || MessageSquare;
                
                return (
                  <div 
                    key={thread.id}
                    onClick={() => handleSelectThread(thread)}
                    style={{
                      padding: 'var(--space-md)',
                      borderBottom: '1px solid var(--glass-border)',
                      cursor: 'pointer',
                      background: selectedThread?.id === thread.id ? 'var(--accent-primary-muted)' : 'transparent',
                      position: 'relative'
                    }}
                    data-testid={`thread-${thread.id}`}
                  >
                    {/* Emergency: Red severity marker - pinned, cannot be dismissed */}
                    {thread.is_emergency && (
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        background: 'var(--status-danger)'
                      }} />
                    )}
                    
                    <div style={{ display: 'flex', gap: 'var(--space-sm)', paddingLeft: thread.is_emergency ? '8px' : 0 }}>
                      {/* Channel Icon */}
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: 'var(--radius-sm)',
                        background: thread.is_emergency ? 'var(--status-danger-bg)' : 'var(--glass-bg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: thread.is_emergency ? 'var(--status-danger)' : 'var(--text-muted)',
                        flexShrink: 0
                      }}>
                        <ChannelIcon size={16} />
                      </div>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Subject - Bold if unread */}
                        <div style={{ 
                          fontSize: '0.875rem',
                          fontWeight: thread.is_unread ? '600' : '500',
                          color: thread.is_emergency ? 'var(--status-danger)' : 'var(--text-primary)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-xs)'
                        }}>
                          {thread.subject}
                          {/* Unread indicator */}
                          {thread.is_unread && !thread.is_emergency && (
                            <span style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              background: 'var(--accent-primary)',
                              flexShrink: 0
                            }} />
                          )}
                        </div>
                        
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {thread.client_name}
                        </div>
                        
                        {/* Emergency status label */}
                        {thread.is_emergency && (
                          <div style={{ 
                            fontSize: '0.625rem', 
                            color: 'var(--status-danger)',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            marginTop: '4px'
                          }}>
                            {thread.status === 'escalating' ? 'Escalating' : 
                             thread.status === 'awaiting_acceptance' ? 'Awaiting Trade Acceptance' :
                             thread.status === 'accepted' ? 'Trade Accepted' : thread.status}
                          </div>
                        )}
                      </div>
                      
                      {/* Timestamp */}
                      <div style={{ fontSize: '0.6875rem', color: 'var(--text-dimmed)', flexShrink: 0 }}>
                        {formatRelativeTime(thread.last_message_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: 'var(--space-xl)' }}>
              <EmptyState 
                title="No conversations" 
                description="Communications will appear here" 
              />
            </div>
          )}
        </div>

        {/* CENTRE: Conversation Thread */}
        {selectedThread && (
          <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', padding: 0 }} data-testid="conversation-thread">
            
            {/* Emergency Thread: Red banner at top */}
            {selectedThread.is_emergency && (
              <div style={{
                background: 'var(--status-danger)',
                color: 'white',
                padding: 'var(--space-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }} data-testid="emergency-banner">
                <AlertTriangle size={18} />
                <span style={{ fontWeight: '600' }}>Emergency Mode Active — Escalation in Progress</span>
              </div>
            )}
            
            {/* Thread Header */}
            <div style={{ padding: 'var(--space-md)', borderBottom: '1px solid var(--glass-border)' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
                {selectedThread.subject}
              </h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                {selectedThread.client_name} • {selectedThread.message_count} messages
              </div>
            </div>
            
            {/* Messages Timeline - Complete, immutable, all entries visible */}
            <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-md)' }} data-testid="message-timeline">
              {selectedThread.messages.map((msg) => (
                <div 
                  key={msg.id}
                  style={{
                    marginBottom: 'var(--space-md)',
                    padding: 'var(--space-md)',
                    background: msg.is_system_event ? 'var(--bg-tertiary)' : 
                               msg.actor_type === 'client' ? 'var(--glass-bg)' : 
                               msg.actor_type === 'assistant' ? 'var(--accent-primary-muted)' : 'var(--glass-bg-hover)',
                    borderRadius: 'var(--radius-md)',
                    borderLeft: msg.is_system_event ? '3px solid var(--text-dimmed)' :
                               msg.actor_type === 'client' ? '3px solid var(--text-muted)' : 
                               msg.actor_type === 'assistant' ? '3px solid var(--accent-primary)' :
                               '3px solid var(--status-success)'
                  }}
                  data-testid={`message-${msg.id}`}
                >
                  {/* Actor label and timestamp - always visible */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-sm)' }}>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      fontWeight: '600', 
                      color: msg.is_system_event ? 'var(--text-dimmed)' : 'var(--text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}>
                      {msg.is_system_event ? 'System' : actorLabels[msg.actor_type] || msg.actor_type}
                      {msg.sender_name && !msg.is_system_event && ` • ${msg.sender_name}`}
                    </span>
                    {/* Timestamp visible on all entries */}
                    <span style={{ fontSize: '0.6875rem', color: 'var(--text-dimmed)' }}>
                      {formatTimestamp(msg.created_at)}
                    </span>
                  </div>
                  
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: msg.is_system_event ? 'var(--text-muted)' : 'var(--text-primary)', 
                    lineHeight: 1.6, 
                    margin: 0,
                    fontStyle: msg.is_system_event ? 'italic' : 'normal'
                  }}>
                    {msg.content}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Manual Reply Box - visible if role permits */}
            <div style={{ 
              padding: 'var(--space-md)', 
              borderTop: '1px solid var(--glass-border)',
              background: 'var(--bg-secondary)'
            }}>
              <textarea
                className="form-input"
                placeholder="Type your reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                rows={2}
                style={{ resize: 'none', marginBottom: 'var(--space-sm)' }}
                data-testid="reply-input"
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSendReply}
                  disabled={!replyContent.trim()}
                  data-testid="send-reply-btn"
                >
                  Send Reply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RIGHT: Context Panel (Job, Client, Status) */}
        {selectedThread && (
          <div className="glass-panel" style={{ padding: 'var(--space-md)' }} data-testid="context-panel">
            <h4 style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-md)' }}>
              Context
            </h4>
            
            {/* Client Info */}
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                <User size={14} style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Client</span>
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                {selectedThread.client_name}
              </div>
            </div>
            
            {/* Job Info */}
            {selectedThread.job_id && (
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                  <Briefcase size={14} style={{ color: 'var(--text-muted)' }} />
                  <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Job</span>
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  {selectedThread.job_title || selectedThread.job_id}
                </div>
              </div>
            )}
            
            {/* Status */}
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                <Clock size={14} style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</span>
              </div>
              <div style={{ 
                fontSize: '0.875rem', 
                color: selectedThread.is_emergency ? 'var(--status-danger)' : 'var(--text-primary)',
                fontWeight: selectedThread.is_emergency ? '600' : '400'
              }}>
                {selectedThread.is_emergency ? (
                  selectedThread.status === 'escalating' ? 'Escalating' :
                  selectedThread.status === 'awaiting_acceptance' ? 'Awaiting Trade Acceptance' :
                  selectedThread.status === 'accepted' ? 'Trade Accepted' : 'Emergency'
                ) : 'Normal'}
              </div>
            </div>
            
            {/* Channel */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                {React.createElement(channelIcons[selectedThread.channel] || MessageSquare, { size: 14, style: { color: 'var(--text-muted)' } })}
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Channel</span>
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                {selectedThread.channel.replace('_', ' ')}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Communications;
